/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Post Payment modal configurations and constants
 */
import { POST_PAYMENT_STATE } from 'app-constants/claims';

/**
 * Default modal width for post payment modals
 */
const DEFAULT_MODAL_WIDTH = 500;

/**
 * Post payment modes
 */
const POST_PAYMENT_MODES: Record<string, PostPayment.IPostPaymentMode> = {
	view: 'view',
	manual: 'manual',
	select: 'select',
};

const POST_PAYMENT_ACTIONS: Record<string, 'post' | 'close' | 'manual'> = {
	post: 'post',
	close: 'close',
	manual: 'manual',
};

/**
 * Modal configuration for post payment button based on claim state
 */
const POST_PAYMENT_MODAL_CONFIG: Record<
	string,
	{ shouldShowModal: boolean; modalProps?: { title: string; content: string; okText: string; cancelText: string } }
> = {
	[POST_PAYMENT_STATE.eraClaim]: {
		shouldShowModal: false,
	},
	[POST_PAYMENT_STATE.autoPosted]: {
		shouldShowModal: false,
	},
	[POST_PAYMENT_STATE.manualPosted]: {
		shouldShowModal: true,
		modalProps: {
			title: 'Confirmation',
			content: 'This claim was manually posted already.\nDo you want to update the posted payment details?',
			okText: 'Yes, Update Posted Claim',
			cancelText: 'Cancel',
		},
	},
	[POST_PAYMENT_STATE.draftClaim]: {
		shouldShowModal: true,
		modalProps: {
			title: 'Enter Payment Manually',
			content: 'Draft payment details exist for this claim.\nEnter manual mode to review or update them?',
			okText: 'Enter Manual Mode',
			cancelText: 'Cancel',
		},
	},
	[POST_PAYMENT_STATE.multiplePayments]: {
		shouldShowModal: false,
	},
	[POST_PAYMENT_STATE.pendingEra]: {
		shouldShowModal: false,
	},
	[POST_PAYMENT_STATE.emptyClaim]: {
		shouldShowModal: false,
	},
	[POST_PAYMENT_STATE.closedClaim]: {
		shouldShowModal: false,
	},
};

/**
 * Post payment states that should hide the post payment button
 */
const HIDDEN_POST_PAYMENT_STATES = [POST_PAYMENT_STATE.autoPosted, POST_PAYMENT_STATE.closedClaim];

const MANUAL_BILLED_MISMATCH_MODAL_COPY = {
	title: 'Review Required: Amount Mismatch',
	content:
		"We noticed that the billed or adjustment totals don't match the claim.\nPlease review and add service line details to balance the amounts.",
};

const MANUAL_PAYMENT_DATE_MODAL = {
	title: 'Review Required: Payment Date Missing',
	content: 'Please enter a payment date in the payment summary before posting.',
};

/**
 * Modal configuration for entering manual mode when no payment data exists
 */
const ENTER_MANUAL_MODE_NO_DATA_MODAL = {
	title: 'Enter Payment Manually',
	content:
		'The insurance payment details for this claim are not available yet.\nYou can proceed with manual entry to record the payment.',
	okText: 'Proceed',
	cancelText: 'Cancel',
};

/**
 * Modal configuration for multiple payment records scenario
 */
const MULTIPLE_PAYMENTS_MODAL = {
	title: 'Multiple Insurance Payments Found',
	content: 'This claim contains multiple insurance payment records(CLPs).\nPlease select one or more records to post before proceeding.',
	okText: 'Select Payment Records',
	cancelText: 'Cancel',
};

/**
 * Error message for selection error modal
 */
const SELECTION_ERROR_MESSAGE = 'Select at least one payment record to continue.';

/**
 * Claim response source identifiers
 */
const CLAIM_RESPONSE_SOURCES = {
	user: 'user',
	x12_277: 'x12_277',
	x12_277ca: 'x12_277ca',
	x12_835: 'x12_835',
	eob: 'eob',
	post_manual: 'post_manual',
	post_era: 'post_era',
} as const;

const INSURANCE_PAYMENT_TAB_SOURCES: string[] = [
	CLAIM_RESPONSE_SOURCES.x12_835,
	CLAIM_RESPONSE_SOURCES.user,
	CLAIM_RESPONSE_SOURCES.x12_277,
	CLAIM_RESPONSE_SOURCES.post_manual,
	CLAIM_RESPONSE_SOURCES.post_era,
];

const DRAFT_CLAIM_BANNER_MESSAGE = 'A draft payment exists for this claim. Select Post Payment to resume.';

const COMMENT_FORM_FIELD = 'comment';

const MODAL_COPY = {
	AUTO_POST: {
		withPendingPatientResponsibility: "You're about to auto-post this claim using the received Insurance Payment Report (ERA) details.",
		withZeroPatientResponsibility:
			"You're about to auto-post this claim using the received Insurance Payment Report (ERA) details.\nSince there is no Patient Responsibility, you may also choose to close the claim.",
	},
	MANUAL_POST: {
		withPendingPatientResponsibility: "You're about to manually post this claim.",
		withZeroPatientResponsibility:
			"You're about to manually post this claim.\nSince there is no Patient Responsibility, you may also choose to close the claim.",
	},
};

export {
	DEFAULT_MODAL_WIDTH,
	POST_PAYMENT_MODAL_CONFIG,
	HIDDEN_POST_PAYMENT_STATES,
	MANUAL_BILLED_MISMATCH_MODAL_COPY,
	ENTER_MANUAL_MODE_NO_DATA_MODAL,
	MANUAL_PAYMENT_DATE_MODAL,
	MULTIPLE_PAYMENTS_MODAL,
	SELECTION_ERROR_MESSAGE,
	POST_PAYMENT_MODES,
	POST_PAYMENT_ACTIONS,
	CLAIM_RESPONSE_SOURCES,
	INSURANCE_PAYMENT_TAB_SOURCES,
	DRAFT_CLAIM_BANNER_MESSAGE,
	COMMENT_FORM_FIELD,
	MODAL_COPY,
};
